import React, { useState } from 'react';
import LZString from 'lz-string';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './po-summary.css';


// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

// Utility function to format numbers with commas and no decimals
const formatNumber = (num) => {
    return num ? num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0";
};

// Utility function to calculate percentage with safeguard against division by zero
const calculatePercentage = (part, total) => {
    if (!total) return 0;
    return ((part / total) * 100).toFixed(1);
};

// Define a nice color palette (you can adjust these colors)
const colorPalette = [
    '#2E86AB', // Blue
    '#A23B72', // Purple
    '#F18F01', // Orange
    '#C73E1D', // Red
    '#3B1F2B', // Dark Purple
    '#44CF6C', // Green
    '#4B3F72', // Dark Blue
    '#F49F0A', // Yellow
    '#2E5266', // Navy
    '#70A288', // Teal
];

const POSummary = ({ inventory, coreProducts }) => {
    const [showPercentages, setShowPercentages] = useState(true);
    const [showRelative, setShowRelative] = useState(false);

    const calculateTagStats = () => {
        const stats = {};
        
        // Initialize stats object for each tag that starts with "stock-"
        inventory.forEach(product => {
            if (Array.isArray(product.tags)) {
                product.tags.forEach(tag => {
                    if (tag.startsWith('stock-')) {
                        if (!stats[tag]) {
                            stats[tag] = {
                                all: { stock: 0 },
                                core: { stock: 0 },
                                poNew: { stock: 0 },
                                final: { stock: 0 },
                                poNew2: { stock: 0 },
                                sales: { 
                                    allTime: { total: 0, core: 0 },
                                    ytd: { total: 0, core: 0 },
                                    thirtyDays: { total: 0, core: 0 }
                                }
                            };
                        }
                    }
                });
            }
        });

        // Calculate sales from orders for different time periods
        try {
            const compressedOrders = sessionStorage.getItem('rawOrdersData');
            const orders = compressedOrders ? JSON.parse(LZString.decompress(compressedOrders)) : [];

            if (Array.isArray(orders)) {
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                
                const startOfYear = new Date('2025-01-01T00:00:00');

                // Create a map of product IDs to their tags for faster lookup
                const productTagsMap = {};
                const coreProductIds = new Set();
                
                // Map product IDs to their tags
                inventory.forEach(product => {
                    if (Array.isArray(product.tags)) {
                        productTagsMap[product.product_id] = product.tags.filter(tag => tag.startsWith('stock-'));
                    }
                });
                
                // Identify core product IDs
                Object.values(coreProducts).forEach(product => {
                    const matchingProduct = inventory.find(p => p.product_id.toString() === Object.keys(coreProducts).find(key => 
                        coreProducts[key].sku === product.sku
                    ));
                    
                    if (matchingProduct) {
                        coreProductIds.add(matchingProduct.product_id.toString());
                    }
                });

                orders.forEach(order => {
                    const orderDate = new Date(order.created_at);
                    const isThirtyDays = orderDate >= thirtyDaysAgo;
                    const isYTD = orderDate >= startOfYear;
                    
                    if (order.line_items) {
                        order.line_items.forEach(item => {
                            const productId = item.product_id?.toString();
                            const tags = productTagsMap[productId];
                            const isCore = coreProductIds.has(productId);
                            
                            if (tags) {
                                const quantity = parseInt(item.quantity) || 0;
                                
                                tags.forEach(tag => {
                                    if (stats[tag]) {
                                        // All time sales
                                        stats[tag].sales.allTime.total += quantity;
                                        if (isCore) {
                                            stats[tag].sales.allTime.core += quantity;
                                        }
                                        
                                        // YTD sales (2025)
                                        if (isYTD) {
                                            stats[tag].sales.ytd.total += quantity;
                                            if (isCore) {
                                                stats[tag].sales.ytd.core += quantity;
                                            }
                                        }
                                        
                                        // 30-day sales
                                        if (isThirtyDays) {
                                            stats[tag].sales.thirtyDays.total += quantity;
                                            if (isCore) {
                                                stats[tag].sales.thirtyDays.core += quantity;
                                            }
                                        }
                                    }
                                });
                            }
                        });
                    }
                });
            }
        } catch (error) {
            console.error('Error processing orders:', error);
        }

        // Calculate All Stock and Core Stock
        inventory.forEach(product => {
            if (Array.isArray(product.tags)) {
                const stockCount = product.variants.reduce((sum, variant) => {
                    return sum + (variant.inventory_quantity || 0);
                }, 0);
                
                product.tags.forEach(tag => {
                    if (stats[tag]) {
                        stats[tag].all.stock += stockCount;
                    }
                });
            }
        });

        // Calculate Core stats
        Object.values(coreProducts).forEach(product => {
            const matchingProduct = inventory.find(p => p.product_id.toString() === Object.keys(coreProducts).find(key => 
                coreProducts[key].sku === product.sku
            ));
            
            if (matchingProduct && Array.isArray(matchingProduct.tags)) {
                matchingProduct.tags.forEach(tag => {
                    if (stats[tag]) {
                        const stockCount = matchingProduct.variants.reduce((sum, variant) => {
                            return sum + (variant.inventory_quantity || 0);
                        }, 0);
                        stats[tag].core.stock += stockCount;
                    }
                });
            }
        });

        // Calculate PO New stats from localStorage (only keep stock)
        inventory.forEach(product => {
            if (Array.isArray(product.tags)) {
                product.variants.forEach(variant => {
                    const poValue = parseInt(localStorage.getItem(`po-stock-input-${variant.id}`)) || 0;
                    if (poValue > 0) {
                        product.tags.forEach(tag => {
                            if (stats[tag]) {
                                stats[tag].poNew.stock += poValue;
                            }
                        });
                    }
                });
            }
        });

        // Calculate Final stats (Core + PO New) - only keep stock
        Object.entries(stats).forEach(([tag, stat]) => {
            stat.final.stock = stat.core.stock + stat.poNew.stock;
        });

        // Calculate PO New2 stats from localStorage (only keep stock)
        inventory.forEach(product => {
            if (Array.isArray(product.tags)) {
                product.variants.forEach(variant => {
                    const poValue = parseInt(localStorage.getItem(`po2-stock-input-${variant.id}`)) || 0;
                    if (poValue > 0) {
                        product.tags.forEach(tag => {
                            if (stats[tag]) {
                                stats[tag].poNew2.stock += poValue;
                            }
                        });
                    }
                });
            }
        });

        return stats;
    };

    const tagStats = calculateTagStats();

    // Calculate totals including sales
    const totals = {
        all: { stock: 0 },
        core: { stock: 0 },
        poNew: { stock: 0 },
        final: { stock: 0 },
        poNew2: { stock: 0 },
        sales: { 
            allTime: { total: 0, core: 0 },
            ytd: { total: 0, core: 0 },
            thirtyDays: { total: 0, core: 0 }
        }
    };

    Object.values(tagStats).forEach(stats => {
        totals.all.stock += stats.all.stock;
        totals.core.stock += stats.core.stock;
        totals.poNew.stock += stats.poNew.stock;
        totals.final.stock += stats.final.stock;
        totals.poNew2.stock += stats.poNew2.stock;

        // Update sales totals
        totals.sales.allTime.total += stats.sales.allTime.total;
        totals.sales.allTime.core += stats.sales.allTime.core;
        totals.sales.ytd.total += stats.sales.ytd.total;
        totals.sales.ytd.core += stats.sales.ytd.core;
        totals.sales.thirtyDays.total += stats.sales.thirtyDays.total;
        totals.sales.thirtyDays.core += stats.sales.thirtyDays.core;
    });

    // Update the calculateMonthlySales function with better error handling
    const calculateMonthlySales = (orders) => {
        const monthlySales = {};
        
        if (Array.isArray(orders)) {
            // Create a map of product IDs to their tags for faster lookup
            const productTagsMap = {};
            inventory.forEach(product => {
                if (Array.isArray(product.tags)) {
                    const productId = product.product_id?.toString();
                    if (productId) {
                        productTagsMap[productId] = product.tags.filter(tag => tag.startsWith('stock-'));
                    }
                }
            });

            console.log('Product Tags Map:', productTagsMap);

            orders.forEach(order => {
                if (!order || !order.line_items) return;

                const orderDate = new Date(order.created_at);
                const monthKey = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, '0')}`;
                
                if (!monthlySales[monthKey]) {
                    monthlySales[monthKey] = {};
                    // Initialize all tags for this month
                    Object.keys(tagStats).forEach(tag => {
                        monthlySales[monthKey][tag] = 0;
                    });
                }

                order.line_items.forEach(item => {
                    if (!item || !item.product_id) return;

                    const productId = item.product_id.toString();
                    const tags = productTagsMap[productId];
                    
                    if (tags && tags.length > 0) {
                        const quantity = parseInt(item.quantity) || 0;
                        tags.forEach(tag => {
                            if (monthlySales[monthKey][tag] !== undefined) {
                                monthlySales[monthKey][tag] += quantity;
                            }
                        });
                    }
                });
            });

            console.log('Monthly Sales Data:', monthlySales);
        }

        return monthlySales;
    };

    // Update the chart data preparation with error handling
    const monthlySalesData = (() => {
        try {
            const compressedOrders = sessionStorage.getItem('rawOrdersData');
            if (!compressedOrders) {
                console.log('No orders data found in session storage');
                return {};
            }

            const orders = JSON.parse(LZString.decompress(compressedOrders));
            if (!Array.isArray(orders)) {
                console.log('Orders data is not an array');
                return {};
            }

            console.log(`Processing ${orders.length} orders`);
            const data = calculateMonthlySales(orders);
            console.log('Processed Monthly Sales Data:', data);
            return data;
        } catch (error) {
            console.error('Error processing orders for chart:', error);
            return {};
        }
    })();

    // Calculate total sales per month for percentages
    const monthlyTotals = {};
    Object.keys(monthlySalesData).forEach(month => {
        monthlyTotals[month] = Object.values(monthlySalesData[month]).reduce((sum, val) => sum + val, 0);
    });

    // Calculate total sales for each category for sorting
    const categoryTotals = Object.entries(tagStats).reduce((acc, [tag, _]) => {
        acc[tag] = Object.keys(monthlySalesData)
            .sort()
            .reduce((sum, month) => sum + (monthlySalesData[month][tag] || 0), 0);
        return acc;
    }, {});

    // Reverse the sorting order to get highest values on top
    const chartData = {
        labels: Object.keys(monthlySalesData).sort().map(month => {
            const [year, monthNum] = month.split('-');
            return `${monthNum}/${year}`;
        }),
        datasets: Object.entries(tagStats)
            // Sort alphabetically by tag name
            .sort((a, b) => a[0].localeCompare(b[0]))
            .map(([tag, _], index) => {
                const color = colorPalette[index % colorPalette.length];
                return {
                    label: tag.replace('stock-', ''),
                    data: Object.keys(monthlySalesData)
                        .sort()
                        .map(month => monthlySalesData[month][tag] || 0),
                    backgroundColor: color,
                    borderColor: color,
                    borderWidth: 1
                };
            })
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: true,
                title: {
                    display: true,
                    text: 'Month',
                    font: { size: 14, weight: 'bold' }
                }
            },
            y: {
                stacked: true,
                beginAtZero: true,
                title: {
                    display: true,
                    text: showRelative ? 'Percentage (%)' : 'Units Sold',
                    font: { size: 14, weight: 'bold' }
                },
                min: 0,
                max: showRelative ? 100 : undefined,
                ticks: {
                    callback: function(value) {
                        return showRelative ? `${value}%` : value;
                    }
                }
            }
        },
        plugins: {
            title: {
                display: true,
                text: 'Monthly Sales Count #',
                font: {
                    size: 18,
                    weight: 'bold'
                },
                padding: 20
            },
            legend: {
                display: true,
                position: 'right',
                reverse: false,
                labels: {
                    boxWidth: 15,
                    padding: 15,
                    font: {
                        size: 12
                    }
                }
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function(context) {
                        const monthKey = Object.keys(monthlySalesData).sort()[context.dataIndex];
                        const originalValue = showRelative ? 
                            context.dataset.originalData[context.dataIndex] : 
                            context.raw;
                        const monthTotal = monthlyTotals[monthKey];
                        const percentage = ((originalValue / monthTotal) * 100).toFixed(1);
                        
                        if (showRelative) {
                            return `${context.dataset.label}: ${percentage}%`;
                        }
                        return `${context.dataset.label}: ${formatNumber(originalValue)} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                display: true,
                formatter: function(value, context) {
                    const monthKey = Object.keys(monthlySalesData).sort()[context.dataIndex];
                    const originalValue = showRelative ? 
                        context.dataset.originalData[context.dataIndex] : 
                        value;
                    const monthTotal = monthlyTotals[monthKey];
                    const percentage = ((originalValue / monthTotal) * 100).toFixed(1);
                    
                    return showPercentages ? `${percentage}%` : formatNumber(originalValue);
                },
                color: 'white',
                font: { weight: 'bold' },
                anchor: 'center',
                align: 'center'
            }
        }
    };

    // Update the data transformation for relative view
    if (showRelative) {
        // Store original data for percentage calculations
        chartData.datasets = chartData.datasets.map(dataset => ({
            ...dataset,
            originalData: [...dataset.data], // Store original values
            data: dataset.data.map((value, index) => {
                const monthKey = Object.keys(monthlySalesData).sort()[index];
                const total = monthlyTotals[monthKey];
                return total > 0 ? (value / total) * 100 : 0;
            })
        }));
    }

    return (
        <div className="tag-summary">
            <h3>Category Summary</h3>
            <div className="summary-table">
                <table>
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th>All Time</th>
                            <th>YTD (2025)</th>
                            <th>30 Day Sales</th>
                            <th>All</th>
                            <th>Core</th>
                            <th>PO New</th>
                            <th>Final</th>
                            <th>PO New2</th>
                        </tr>
                        <tr>
                            <th></th>
                            <th>all | core %</th>
                            <th>all | core %</th>
                            <th>all | core %</th>
                            <th>Stock</th>
                            <th>Stock</th>
                            <th>Stock</th>
                            <th>Stock</th>
                            <th>Stock</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Object.entries(tagStats).map(([tag, stats]) => (
                            <tr key={tag}>
                                <td className="category-col">{tag.replace('stock-', '')}</td>
                                <td className="sales-col core-data">
                                    <div className="core-all-container">
                                        <span>{formatNumber(stats.sales.allTime.total)}</span>
                                        <span className="core-all-divider">|</span>
                                        <span className="core-value">{formatNumber(stats.sales.allTime.core)} </span>
                                        <span className="core-percentage">
                                            ⋮  {calculatePercentage(stats.sales.allTime.core, stats.sales.allTime.total)}%
                                        </span>
                                    </div>
                                </td>
                                <td className="sales-col core-data">
                                    <div className="core-all-container">
                                        <span>{formatNumber(stats.sales.ytd.total)}</span>
                                        <span className="core-all-divider">|</span>
                                        <span className="core-value">{formatNumber(stats.sales.ytd.core)}</span>
                                        <span className="core-percentage">
                                            ⋮  {calculatePercentage(stats.sales.ytd.core, stats.sales.ytd.total)}%
                                        </span>
                                    </div>
                                </td>
                                <td className="sales-col core-data">
                                    <div className="core-all-container">
                                        <span>{formatNumber(stats.sales.thirtyDays.total)}</span>
                                        <span className="core-all-divider">|</span>
                                        <span className="core-value">{formatNumber(stats.sales.thirtyDays.core)}</span>
                                        <span className="core-percentage">
                                            ⋮  {calculatePercentage(stats.sales.thirtyDays.core, stats.sales.thirtyDays.total)}%
                                        </span>
                                    </div>
                                </td>
                                <td className="stock-col">{formatNumber(stats.all.stock)}</td>
                                <td className="stock-col core-data">
                                    <div className="core-all-container">
                                        <span>{formatNumber(stats.core.stock)}</span>
                                        <span className="core-percentage">
                                            ⋮  {calculatePercentage(stats.core.stock, stats.all.stock)}%
                                        </span>
                                    </div>
                                </td>
                                <td className="stock-col">{formatNumber(stats.poNew.stock)}</td>
                                <td className="stock-col">{formatNumber(stats.final.stock)}</td>
                                <td className="stock-col">{formatNumber(stats.poNew2.stock)}</td>
                            </tr>
                        ))}
                        <tr>
                            <td className="category-col"><strong>Total</strong></td>
                            <td className="sales-col core-data">
                                <div className="core-all-container">
                                    <strong>{formatNumber(totals.sales.allTime.total)}</strong>
                                    <span className="core-all-divider">|</span>
                                    <strong className="core-value">{formatNumber(totals.sales.allTime.core)}</strong>
                                    <span className="core-percentage">
                                        ⋮  {calculatePercentage(totals.sales.allTime.core, totals.sales.allTime.total)}%
                                    </span>
                                </div>
                            </td>
                            <td className="sales-col core-data">
                                <div className="core-all-container">
                                    <strong>{formatNumber(totals.sales.ytd.total)}</strong>
                                    <span className="core-all-divider">|</span>
                                    <strong className="core-value">{formatNumber(totals.sales.ytd.core)}</strong>
                                    <span className="core-percentage">
                                        ⋮  {calculatePercentage(totals.sales.ytd.core, totals.sales.ytd.total)}%
                                    </span>
                                </div>
                            </td>
                            <td className="sales-col core-data">
                                <div className="core-all-container">
                                    <strong>{formatNumber(totals.sales.thirtyDays.total)}</strong>
                                    <span className="core-all-divider">|</span>
                                    <strong className="core-value">{formatNumber(totals.sales.thirtyDays.core)}</strong>
                                    <span className="core-percentage">
                                        ⋮  {calculatePercentage(totals.sales.thirtyDays.core, totals.sales.thirtyDays.total)}%
                                    </span>
                                </div>
                            </td>
                            <td className="stock-col">
                                <strong>{formatNumber(totals.all.stock)}</strong>
                            </td>
                            <td className="stock-col core-data">
                                <div className="core-all-container">
                                    <strong>{formatNumber(totals.core.stock)}</strong>
                                    <span className="core-percentage">
                                        ⋮  {calculatePercentage(totals.core.stock, totals.all.stock)}%
                                    </span>
                                </div>
                            </td>
                            <td className="stock-col">
                                <strong>{formatNumber(totals.poNew.stock)}</strong>
                            </td>
                            <td className="stock-col">
                                <strong>{formatNumber(totals.final.stock)}</strong>
                            </td>
                            <td className="stock-col">
                                <strong>{formatNumber(totals.poNew2.stock)}</strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="monthly-sales-chart" style={{ 
                marginTop: '2rem', 
                height: '600px',
                padding: '20px',
                overflow: 'hidden'
            }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '1rem'
                }}>
                    <h3>Monthly Sales by Units</h3>
                    <div className="display-toggle" style={{ display: 'flex', gap: '20px' }}>
                        <div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={showPercentages}
                                    onChange={() => setShowPercentages(!showPercentages)}
                                />
                                <span className="slider round"></span>
                            </label>
                            <span style={{ marginLeft: '8px' }}>
                                Show {showPercentages ? 'Percentages' : 'Values'}
                            </span>
                        </div>
                        <div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={showRelative}
                                    onChange={() => setShowRelative(!showRelative)}
                                />
                                <span className="slider round"></span>
                            </label>
                            <span style={{ marginLeft: '8px' }}>
                                {showRelative ? 'Relative' : 'Absolute'} View
                            </span>
                        </div>
                    </div>
                </div>
                <div style={{ height: 'calc(100% - 60px)' }}>
                    <Bar data={chartData} options={chartOptions} />
                </div>
            </div>
        </div>
    );
};

export default POSummary; 